/**
 * Intake's settings shell — four sections, one rendered at a time, with
 * the active one a URL place (`?section=`, committed with `replace`) so a
 * section is linkable and survives Back. SETTINGS_SECTIONS is the one
 * registry a new section is added to.
 *
 * A section is one screen, and three of them open a page below it: Phone
 * opens the **Receptionist**, Questions opens the **Estimate** those
 * questions add up to, and General opens **Rental system** and
 * **Users** (`?page=`, the section's own to set). What decides whether
 * something is a section or a page is how often it is opened, not how
 * big it is: the rental system and the sign-in list are answered on the
 * day a Company starts and touched about never again, so as destinations
 * they were a third of the sidebar's height that nobody opens twice.
 *
 * The sidebar is still the navigation, and a section reached through it
 * opens at most this one step. There was an Intake section holding
 * Phone, Questions and the notification email as three rooms to walk
 * into, which put the one list a Company actually edits at the same
 * depth as a number's plumbing, and a switch three drills from a sidebar
 * that was already pointing at it. That is the shape this rule is
 * against — rooms holding rooms — and not a screen that names the two
 * pages under it.
 *
 * A page under the bar, not the whole app. It was the whole app until
 * #85 — intake had no list to browse, so `/` was this page — and it is
 * reached from the account menu now, the way the RMS workspace's settings
 * are: configuration is not a destination an Operator moves between.
 *
 * The Company-settings fetch lives here rather than in each section
 * because two sections read from the same row and a partial PUT returns
 * the whole row — one fetch, one copy, and a save in General cannot
 * leave the Receptionist's facts reading a stale value.
 */
import { useCallback, useEffect, type ReactNode } from "react";
import { useSearchParams } from "react-router";
import { AdminPageHeader } from "../../components/ui/AdminPageHeader";
import { Banner } from "../../components/ui/Banner";
import { ChevronLeftIcon, ChevronRightIcon } from "../../components/ui/icons";
import {
  getCompanySettings,
  saveCompanySettings,
  type CompanySettings,
  type CompanySettingsUpdate,
} from "../../lib/settings-api";
import { useSettingsSource } from "../../lib/use-settings-source";
import { Loading } from "./section-chrome";
import { ConnectionsSection } from "./ConnectionsSection";
import { GeneralSection } from "./GeneralSection";
import { InquiryEmailSection } from "./InquiryEmailSection";
import { InquiryQuestions } from "./InquiryQuestions";
import { PhoneSection } from "./PhoneSection";

import styles from "./index.module.css";

/** Everything a settings-backed section may need from the shell. */
type SectionContext = {
  settings: CompanySettings;
  onPatch: (patch: CompanySettingsUpdate) => Promise<void>;
  onLogoChanged: (logoURL: string) => void;
};

type SettingsSection = {
  id: string;
  label: string;
} & (
  | {
      /** Section reads the company-settings fetch; shell gates on Loading. */
      needsSettings: true;
      render: (ctx: SectionContext) => ReactNode;
    }
  | { needsSettings: false; render: () => ReactNode }
);

/** One row per section: id (the `?section=` value), label, data need, body. */
const SETTINGS_SECTIONS: SettingsSection[] = [
  {
    // Who the Company is, and one step below it the one thing set up on
    // the first day and left alone after: who can sign in.
    id: "general",
    label: "General",
    needsSettings: true,
    render: (ctx) => (
      <GeneralSection
        settings={ctx.settings}
        onPatch={ctx.onPatch}
        onLogoChanged={ctx.onLogoChanged}
      />
    ),
  },
  {
    // What a Customer is asked, on the form and on the phone alike: one
    // list, and the one a Company edits most (ADR-0022). It sits above
    // the number rather than inside it for that reason.
    id: "questions",
    label: "Questions",
    needsSettings: false,
    render: () => <InquiryQuestions />,
  },
  {
    // Where a call rings, what a caller hears before anyone picks up, and
    // one step below it, the Receptionist that answers when nobody does.
    // It reads the company-settings row for the notes on that page.
    id: "phone",
    label: "Phone",
    needsSettings: true,
    render: (ctx) => (
      <PhoneSection settings={ctx.settings} onPatch={ctx.onPatch} />
    ),
  },
  {
    // The one email intake sends, and now every part of who receives it:
    // the address it goes to, the switch that stops it, and everyone
    // else copied on it. Named for what it is to the Company rather than
    // for the thing it is about — Inquiries is the destination above
    // these settings since #85, and one name cannot be both a place you
    // read and a place you configure.
    id: "notifications",
    label: "Notifications",
    needsSettings: true,
    render: (ctx) => (
      <InquiryEmailSection settings={ctx.settings} onPatch={ctx.onPatch} />
    ),
  },
  {
    // The outside systems intake writes into — the rental system and the
    // calendar — each one step below, where it is connected or dropped.
    // Last, because they are set up once; a section, because a Company
    // looking for what it plugged in looks for this word.
    id: "connections",
    label: "Connections",
    needsSettings: false,
    render: () => <ConnectionsSection />,
  },
];

export default function SettingsPage() {
  // No/unknown `?section=` shows General on desktop and the drill-down
  // root list on mobile.
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = SETTINGS_SECTIONS.find(
    (s) => s.id === searchParams.get("section"),
  );
  const active = requested ?? SETTINGS_SECTIONS[0];
  // Mobile drill-down: the section list IS the screen; picking one pushes the
  // section with a Back link. Desktop (sm+) ignores this and keeps two panes.
  const mobileView = requested ? "section" : "list";

  const {
    data: settings,
    setData: setSettings,
    loadError,
    setLoadError,
  } = useSettingsSource(getCompanySettings);

  useEffect(() => {
    document.title = "Settings | HireOS";
  }, []);

  /** Partial PUT; rows await this and render failures inline. */
  const onPatch = useCallback(async (patch: CompanySettingsUpdate) => {
    const next = await saveCompanySettings(patch);
    setSettings(next);
  }, []);

  const onLogoChanged = useCallback((logoURL: string) => {
    setSettings((current) =>
      current ? { ...current, logo_url: logoURL } : current,
    );
  }, []);

  // `?page=` belongs to whichever section set it, so leaving a section
  // takes it with them — a sidebar click must never land on a page of
  // the section that was open before it.
  const select = (id: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("section", id);
    next.delete("page");
    setSearchParams(next, { replace: true });
  };

  const backToList = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("section");
    next.delete("page");
    setSearchParams(next, { replace: true });
  };

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <AdminPageHeader
          title="Settings"
          subtitle="Your company, your number, and where your jobs go"
        />

        {loadError && (
          <Banner
            variant="error"
            className={styles.loadBanner}
            onDismiss={() => setLoadError(null)}
          >
            {loadError}
          </Banner>
        )}

        <div className={styles.layout}>
          {/* One landmark for both presentations: the sidebar list (sm+) and
              the mobile drill-down root are the same nav rendered twice. */}
          <nav
            aria-label="Settings sections"
            className={`${styles.nav} ${
              mobileView === "list" ? "" : styles.navWhileSectionOpen
            }`}
          >
            <ul className={styles.sectionList}>
              {SETTINGS_SECTIONS.map((s) => (
                <li key={s.id}>
                  <button
                    type="button"
                    aria-current={active.id === s.id ? "page" : undefined}
                    onClick={() => select(s.id)}
                    className={`${styles.sectionItem} ${
                      active.id === s.id ? styles.sectionItemActive : ""
                    }`}
                  >
                    {s.label}
                  </button>
                </li>
              ))}
            </ul>

            {/* Mobile drill-down root: section rows, label + chevron */}
            {mobileView === "list" && (
              <ul className={styles.drillList}>
                {SETTINGS_SECTIONS.map((s) => (
                  <li key={s.id}>
                    <button
                      type="button"
                      onClick={() => select(s.id)}
                      className={styles.drillRow}
                    >
                      <span className={styles.drillLabel}>{s.label}</span>
                      <ChevronRightIcon className={styles.drillChevron} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </nav>

          <div
            className={`${styles.content} ${
              mobileView === "list" ? styles.contentWhileListOpen : ""
            }`}
          >
            {mobileView === "section" && (
              <button
                type="button"
                onClick={backToList}
                className={styles.backLink}
              >
                <ChevronLeftIcon className={styles.backIcon} />
                Settings
              </button>
            )}

            {active.needsSettings ? (
              settings ? (
                active.render({ settings, onPatch, onLogoChanged })
              ) : (
                <Loading />
              )
            ) : (
              active.render()
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
